import {
  useState,
  MouseEvent,
  KeyboardEvent,
  ElementType,
  useEffect,
  useRef,
} from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import StorageIcon from '@mui/icons-material/Storage';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import { useLocation, useNavigate } from 'react-router-dom';
import SideMenu from './SideMenu';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../stores/store';
import { postLogout } from '../../stores/userInfo/slice';
import ErrorModal from '../Modal/ErrorModal';
import useErrorModal from '../../hooks/useErrorModal';
import { INSPEX_PATHNAME } from '../../_constants/Const';
import { isManagerOrAdmin } from '../../_utils/utils';
import { ManageHistory } from '@mui/icons-material';
import { isRejected } from '@reduxjs/toolkit';
import { API_CODE } from '../../_constants/apiConst';
import { PRINT_REPORT_NOT_DISPLAY } from '../../_constants/styleConst';
import { isEmptyData } from '../../_utils/utils';
import ConfirmModal from '../Modal/ConfirmModal';

type MenuItemType = {
  label: string;
  path: string;
  icon: ElementType;
};

/** 修理依頼検索のメニュー */
const searchMenu: MenuItemType = {
  label: 'LBL_SEARCH_REPAIR_REQUEST',
  path: INSPEX_PATHNAME.RMA_SEARCH,
  icon: SearchIcon,
};

/** 管理系画面のメニューリスト */
const manageMenuItems: MenuItemType[] = [
  {
    label: 'LBL_INSPECTION_ITEM_MANAGEMENT',
    path: INSPEX_PATHNAME.MANAGE_INSPECTION_ITEM,
    icon: AssignmentIcon,
  },
  {
    label: 'LBL_CRITERIA_MANAGEMENT',
    path: INSPEX_PATHNAME.MANAGE_CRITERIA,
    icon: SquareFootIcon,
  },
  {
    label: 'LBL_TEMPLATE_MANAGEMENT',
    path: INSPEX_PATHNAME.MANAGE_TEMPLATE,
    icon: ChecklistIcon,
  },
  {
    label: 'LBL_USER_MANAGEMENT',
    path: INSPEX_PATHNAME.MANAGE_USER,
    icon: PersonIcon,
  },
  {
    label: 'LBL_ORGANIZATION_MANAGEMENT',
    path: INSPEX_PATHNAME.MANAGE_ORGANIZATION,
    icon: BusinessIcon,
  },
  {
    label: 'LBL_MASTER_MANAGEMENT',
    path: INSPEX_PATHNAME.MANAGE_CODE_MASTER,
    icon: StorageIcon,
  },
  {
    label: 'LBL_HISTORY_MANAGEMENT',
    path: '/ManageHistory',
    icon: ManageHistory,
  },
];

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const [isOpenLogoutDialog, setIsOpenLogoutDialog] = useState<boolean>(false);
  const [isOpenSideMenu, setIsOpenSideMenu] = useState<boolean>(false);
  const [isOpenInspectionConfirm, setIsOpenInspectionConfirm] =
    useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
  const { userData } = useSelector((state: RootState) => state.userInfo);
  const { error, showApiError, hideError } = useErrorModal();
  const [selectedPath, setSelectedPath] = useState<string>('');

  /** 処理中フラグ */
  const isProcessing = useRef(false);

  useEffect(() => {
    // ログインしていない
    if (isEmptyData(userData.userId)) {
      setMenuItems([]);
      return;
    }
    const menuItems = [searchMenu];
    if (isManagerOrAdmin(userData)) {
      menuItems.push(...manageMenuItems);
    }
    setMenuItems(menuItems);
  }, [userData]);

  const handleLogoutClick = async () => {
    setIsOpenLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    const response = await dispatch(postLogout());
    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.LOGOUT);
      return;
    }
    navigate(INSPEX_PATHNAME.LOGIN);
  };

  const onClickLogoutConfirmOk = async () => {
    setIsOpenLogoutDialog(false);
    if (isProcessing.current) {
      // 連打防止
      return;
    }
    isProcessing.current = true;
    try {
      await handleLogoutConfirm();
    } finally {
      isProcessing.current = false;
    }
  };

  const handleLogoutCancel = () => {
    setIsOpenLogoutDialog(false);
  };

  const toggleSideMenu =
    (open: boolean) => (event: KeyboardEvent | MouseEvent) => {
      if (
        (event.type === 'keydown' && (event as KeyboardEvent).key === 'Tab') ||
        (event as KeyboardEvent).key === 'Shift'
      ) {
        return;
      }
      setIsOpenSideMenu(open);
    };

  const handleSideMenuItemClick = (path: string) => {
    setIsOpenSideMenu(false);
    if (location.pathname.includes(INSPEX_PATHNAME.INSPECTION)) {
      // 診断画面は入力内容破棄の確認モーダルを表示する
      setIsOpenInspectionConfirm(true);
      setSelectedPath(path);
      return;
    }
    navigate(path);
  };

  /** 診断画面の入力内容破棄確認モーダルOK押下 */
  const handleInspectionConfirmOk = () => {
    navigate(selectedPath);
    setIsOpenInspectionConfirm(false);
  };

  /** 診断画面の入力内容破棄確認モーダルキャンセル押下 */
  const handleInspectionConfirmCancel = () => {
    setIsOpenInspectionConfirm(false);
  };

  return (
    <>
      <AppBar position="fixed" sx={PRINT_REPORT_NOT_DISPLAY}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* ハンバーガーメニューアイコン */}
            {menuItems.length > 0 && (
              <IconButton
                onClick={toggleSideMenu(true)}
                sx={{ color: '#FFFFFF' }}
              >
                <MenuIcon sx={{ fontSize: '2.5rem', padding: '0px' }} />
              </IconButton>
            )}
            {/* ロゴ */}
            <Box
              component="img"
              src="/logo/logo.svg"
              alt="ロゴ"
              sx={{
                pointerEvents: 'none',
                scale: 1.5,
                alignContent: 'center',
                pl: 2,
              }}
            />
          </Box>

          {/* 右側のエリア */}
          {!isEmptyData(userData.userId) && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                <PersonIcon sx={{ mr: 1 }}></PersonIcon>
                <Typography variant="body1" sx={{ mr: 1 }}>
                  {userData.name}
                </Typography>
              </Box>
              <Button
                color="inherit"
                onClick={handleLogoutClick}
                sx={{ ml: 2 }}
              >
                {t('BTN_LOGOUT')}
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      <SideMenu
        isOpen={isOpenSideMenu}
        menuItems={menuItems}
        onClickClose={toggleSideMenu(false)}
        onClickNavigate={handleSideMenuItemClick}
      />

      {/* ログアウト確認のポップアップ */}
      <Dialog
        open={isOpenLogoutDialog}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        closeAfterTransition={false}
      >
        <DialogTitle id="logout-dialog-title">{t('LBL_LOGOUT')}</DialogTitle>
        {/* ログアウト確認 */}
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            {t('MSG_LOGOUT')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="primary">
            {t('BTN_CANCEL')}
          </Button>
          <Button
            onClick={() => onClickLogoutConfirmOk()}
            color="primary"
            autoFocus
            disabled={isProcessing.current}
          >
            {t('BTN_LOGOUT')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 診断画面の入力内容破棄確認モーダル */}
      <ConfirmModal
        isOpen={isOpenInspectionConfirm}
        displayMessage={t('MSG_TRANSITION_PAGE')}
        onClickCancel={handleInspectionConfirmCancel}
        onClickOk={handleInspectionConfirmOk}
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
    </>
  );
};

export default Header;
